// @ts-nocheck
import type { ExportFormat, ChartExportOptions } from '../../types';

/**
 * Export chart utilities
 *
 * Provides functions to export charts as PNG, SVG, or CSV
 */

/**
 * Download a file with the given content
 */
function downloadFile(content: string | Blob, filename: string, mimeType: string) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export chart as PNG image
 * Uses html2canvas to capture the chart element
 */
export async function exportAsPNG(
  element: HTMLElement,
  filename: string = 'chart.png',
  width?: number,
  height?: number
) {
  try {
    // Dynamically import html2canvas to reduce bundle size
    const html2canvas = await import('html2canvas');

    const canvas = await html2canvas.default(element, {
      backgroundColor: null,
      scale: 2, // Higher quality
      width,
      height,
    });

    canvas.toBlob((blob) => {
      if (blob) {
        downloadFile(blob, filename, 'image/png');
      }
    });
  } catch (error) {
    console.error('Failed to export as PNG:', error);
    // Fallback: try using canvas directly if html2canvas fails
    fallbackToPNGExport(element, filename);
  }
}

/**
 * Fallback PNG export using canvas (for SVG charts from Recharts)
 */
function fallbackToPNGExport(element: HTMLElement, filename: string) {
  const svg = element.querySelector('svg');
  if (!svg) {
    console.error('No SVG element found');
    return;
  }

  const svgData = new XMLSerializer().serializeToString(svg);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx?.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);

    canvas.toBlob((blob) => {
      if (blob) {
        downloadFile(blob, filename, 'image/png');
      }
    });
  };

  img.src = url;
}

/**
 * Export chart as SVG
 */
export function exportAsSVG(element: HTMLElement, filename: string = 'chart.svg') {
  const svg = element.querySelector('svg');
  if (!svg) {
    console.error('No SVG element found');
    return;
  }

  // Clone the SVG to avoid modifying the original
  const clonedSvg = svg.cloneNode(true) as SVGElement;

  // Add XML namespace if not present
  if (!clonedSvg.getAttribute('xmlns')) {
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  // Serialize the SVG
  const svgData = new XMLSerializer().serializeToString(clonedSvg);

  // Add XML declaration
  const svgBlob = new Blob(
    ['<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n' + svgData],
    { type: 'image/svg+xml;charset=utf-8' }
  );

  downloadFile(svgBlob, filename, 'image/svg+xml');
}

/**
 * Export data as CSV
 */
export function exportAsCSV<T extends Record<string, any>>(
  data: T[],
  filename: string = 'data.csv',
  columns?: (keyof T)[]
) {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Determine columns
  const keys = columns || (Object.keys(data[0]) as (keyof T)[]);

  // Create CSV header
  const header = keys.join(',');

  // Create CSV rows
  const rows = data.map((row) => {
    return keys
      .map((key) => {
        let value = row[key];

        // Handle different data types
        if (value === null || value === undefined) {
          return '';
        }

        if (value instanceof Date) {
          value = value.toISOString();
        }

        // Escape quotes and wrap in quotes if contains comma or quote
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      })
      .join(',');
  });

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  downloadFile(csv, filename, 'text/csv;charset=utf-8');
}

/**
 * Main export function that delegates to the appropriate export method
 */
export async function exportChart(
  element: HTMLElement,
  data: any[],
  options: ChartExportOptions
) {
  const { format, filename, width, height } = options;

  const baseFilename = filename || 'chart';
  const filenameWithExt = baseFilename.includes('.')
    ? baseFilename
    : `${baseFilename}.${format}`;

  switch (format) {
    case 'png':
      await exportAsPNG(element, filenameWithExt, width, height);
      break;
    case 'svg':
      exportAsSVG(element, filenameWithExt);
      break;
    case 'csv':
      exportAsCSV(data, filenameWithExt);
      break;
    default:
      console.error(`Unsupported export format: ${format}`);
  }
}
